import { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs, Zoom, Scrollbar, Mousewheel } from "swiper/modules";
import { PiFlagPennant } from "react-icons/pi";
import { ArrowRightToLine, X, PanelRight, ChevronLeft, ChevronRight, ChevronDown, ZoomIn, ZoomOut, Shrink, Captions, EyeOff, Info, Eye } from "lucide-react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import "swiper/css/zoom";
import "swiper/css/scrollbar";
import "swiper/css/mousewheel";
import { trackEvent as trackPlanEvent } from "common/analytics";
import { entryPageFromCurrentPage } from "@/analytics/entryPage";
import { StandardButton } from "@/components/common/StandardButton";
import SmartTaggingPanel from "@/components/common/SmartTaggingPanel";
import { useI18N } from "common/hooks";

// Define interface for image items with tags
interface ImageItem {
  src: string;
  alt?: string;
  tag_name?: string;
  category_name?: string;
  qtags?: Array<{
    tagId: number;
    tagName: string;
    categoryDisplaySectionId?: string;
  }>;
  tags?: Array<{
    tagName: string;
    categoryDisplaySectionId?: string;
  }>;
  // Display sections for accessibility tags
  displaySections?: Record<string, string>;
  // Venue type to determine rendering behavior
  venueType?: 'Basic' | 'Hybrid';
  // Other properties from review photos
  path?: string;
  review_photos_id?: string | number;
  user_name?: string;
  id?: number;
  tag_applied?: string;
  created_at?: string;
  photo_type?: string;
  width?: number;
  height?: number;
  sort_order?: number;
  // Venue details for tracking
  venueId?: string | number;
  venueName?: string;
  venueAddress?: string;
  venueBrandId?: string | number;
  venueBrandName?: string;
  venueTypes?: string;
  user_roles?: {
    isGeneralUser: boolean | false;
  };
  // Experience / review photo fields
  isExperiencePhoto?: boolean;
  review_user_first_name?: string;
  review_user_last_name?: string;
  review_user_profile_photo?: string;
  review_user_color?: string;
  review_notes?: string;
  review_time_ago?: string;
  review_accessibility_experience?: string | null;
}

// General-user uploads get credited by name; staff/team uploads keep the
// generic "Sociability team" attribution.
const photoAttribution = (
  image: Pick<ImageItem, "user_name" | "user_roles">,
  sociabilityTeamLabel: string,
): string =>
  image.user_roles?.isGeneralUser && image.user_name
    ? image.user_name
    : sociabilityTeamLabel;

interface FullImageViewerProps {
  images: ImageItem[];
  currentIndex: number;
  onClose: () => void;
  onFlag: (image: ImageItem) => void;
  showFlag: boolean;
  showRightTab: boolean;
  /**
   * Frames the tag list as machine-written, matching the space card and the
   * space detail view. Decided by the caller, which knows both whether the
   * tags are AI-generated and whether the venue is team-verified.
   */
  isSmartTagged?: boolean;
  pageName?: string; // For analytics context - defaults to "Venue Photo"
}

// Reusable component for the image details tab content
const ImageDetailsTab = ({ currentImage }: { currentImage: ImageItem }) => {
  const i18n = useI18N();
  return (
  <div className="min-h-[200px]" role="region" aria-label="Image description details">
    {/* Alt text section */}
    {currentImage?.alt && (
      <div className="mb-4 bg-black bg-opacity-30 rounded-md">
        <h3 className="font-semibold text-md mb-2 text-primary-100">{i18n.get("description")}</h3>
        <div className="max-h-[200px] overflow-y-auto pr-2 custom-scrollbar">
          <p className="text-primary-100 text-sm transport-options-content-slider"
            dangerouslySetInnerHTML={{ 
              __html: currentImage.alt ?
                currentImage.alt.replace(/\r\n|\r|\n/g, '<br />') : ''
            }}
          />
        </div>
        <div className="text-xs text-primary-100 mt-2 text-left">
          {currentImage?.created_at && (
            <>
              {new Date(currentImage.created_at).toLocaleDateString()}<span className="mx-2">•</span>
            </>
          )}
          {photoAttribution(currentImage, i18n.get("sociabilityTeam"))}
        </div>
      </div>
    )}
    
    {/* Photo metadata */}
    <div className="mt-6 pt-4 border-t border-gray-800 text-xs text-gray-500">
      {currentImage?.user_name && <p className="mb-1">{i18n.get("addedBy")}: {currentImage.user_name}</p>}
    </div>
  </div>
  );
};

// Reusable component for the accessibility tags tab content
const AccessibilityTagsTab = ({
  currentImage,
  isSmartTagged = false,
}: {
  currentImage: ImageItem;
  isSmartTagged?: boolean;
}) => {
  const i18n = useI18N();
  // Panel replaces the plain wrapper when AI-tagged, as on the space card.
  const TagsWrapper = ({ children }: { children: React.ReactNode }) =>
    isSmartTagged ? (
      <SmartTaggingPanel onDark>{children}</SmartTaggingPanel>
    ) : (
      <>{children}</>
    );
  return (
  <div className="min-h-[200px]"  >
    {/* Accessibility tags section */}
    {currentImage?.qtags && currentImage.qtags.length > 0 ? (
      <div className="mb-4" aria-label="Accessibility tags" role="region">
        <TagsWrapper>
          {(() => {
            // Group tags by their display section
            const sections = currentImage.displaySections || {};
            const sectionMap = new Map<string, string[]>();
            
            // First, organize tags by section
            currentImage.qtags.forEach(tag => {
              const sectionId = tag.categoryDisplaySectionId?.toString() || '';
              const sectionName = sections[sectionId] || 'Other';
              
              if (!sectionMap.has(sectionName)) {
                sectionMap.set(sectionName, []);
              }
              
              sectionMap.get(sectionName)?.push(tag.tagName);
            });
            
            // Get the main tag (first tag)
            const mainTag = currentImage.qtags[0]?.tagName || '';
            
            // Now render the sections
            return Array.from(sectionMap.entries()).map(([sectionName, tagNames], sectionIndex) => {
              // Check if all values match the main tag
              const allValuesMatchMainTag = tagNames.every(value => value === mainTag);
              
              return (
                <div
                  key={sectionName}
                  className={`px-[18px] ${sectionIndex !== sectionMap.size - 1 ? "border-b border-b-gray-500" : ""}`}
                  role="region"
                  aria-label={`${sectionName} accessibility features`}
                >
                  {/* Only show section name if not all values match main tag */}
                  {!allValuesMatchMainTag && (
                    <p className="text-primary-300 dark:text-primary-300 font-medium pt-2">
                      {sectionName === "Main Area" ? i18n.get("general") : `${sectionName}:`}
                    </p>
                  )}
                  
                  {/* List the tags */}
                  <ul className="list-disc px-6 py-1" role="list">
                    {tagNames.map((tagName, tagIndex) => {
                      // For Basic venues: Skip if this tag is the main tag
                      // For Hybrid venues: Always show all tags
                      if (currentImage.venueType === 'Basic' && mainTag === tagName) return null;
                      
                      return (
                        <li key={tagIndex} className="text-primary-100 my-1">
                          {tagName}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            });
          })()} 
        </TagsWrapper>
      </div>
    ) : (
      <div className="text-center py-6 text-primary-100/70" aria-label={i18n.get("noAccessibilityTagsAvailableForThisPhoto")} role="region">
        <p>{i18n.get("noAccessibilityTagsAvailableForThisPhoto")}</p>
      </div>
    )}
  </div>
  );
};

// Reusable component for the experience review tab content
const ReviewTab = ({ currentImage }: { currentImage: ImageItem }) => {
  const isSweet = currentImage.review_accessibility_experience === "cool";
  const hasSourOrSweet = currentImage.review_accessibility_experience != null;
  const userName = [currentImage.review_user_first_name, currentImage.review_user_last_name]
    .filter(Boolean)
    .join(" ") || "User";
  const initial = userName.charAt(0).toUpperCase();
  const timeLabel = currentImage.review_time_ago ||
    (currentImage.created_at ? new Date(currentImage.created_at).toLocaleDateString() : "");

  return (
    <div className="min-h-[200px]">
      {/* User header — matches BusinessReview card style */}
      <div className="flex items-center gap-3 mb-4">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {currentImage.review_user_profile_photo ? (
            <img
              className="w-9 h-9 rounded-full shadow-md object-cover"
              src={currentImage.review_user_profile_photo}
              alt={userName}
            />
          ) : (
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm"
              style={{ backgroundColor: currentImage.review_user_color || "#8751FF" }}
            >
              {initial}
            </div>
          )}
        </div>

        {/* Name + time/experience subtitle */}
        <div className="flex flex-col justify-center gap-0.5 min-w-0">
          <span className="text-primary-100 font-semibold text-sm leading-tight">
            {userName}
          </span>
          <span className="text-primary-100/70 text-xs leading-tight flex items-center flex-wrap gap-x-1">
            {timeLabel && <span>{timeLabel}</span>}
            {timeLabel && hasSourOrSweet && <span>|</span>}
            {hasSourOrSweet && (
              <>
                <span>Shared a</span>
                <img
                  src={isSweet ? "/images/Orange.svg" : "/images/OrangeSlice.svg"}
                  alt=""
                  aria-hidden="true"
                  className="w-4 h-4 inline-block"
                />
                <span>
                  <span className="font-semibold text-primary-100">
                    &ldquo;{isSweet ? "Sweet" : "Sour"}&rdquo;
                  </span>{" "}experience
                </span>
              </>
            )}
          </span>
        </div>
      </div>

      {/* Review text */}
      {currentImage.review_notes ? (
        <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
          <p className="text-primary-100/90 text-sm leading-relaxed">
            {currentImage.review_notes}
          </p>
        </div>
      ) : (
        <p className="text-primary-100/50 text-sm italic">No review text.</p>
      )}
    </div>
  );
};

// Add this near the top of the component after the imports
const CustomScrollbarStyle = () => (
  <style dangerouslySetInnerHTML={{ __html: `
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 3px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: rgba(163, 159, 232, 0.6);
      border-radius: 3px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: rgba(163, 159, 232, 0.8);
    }
  `}} />
);

export default function FullImageViewer({ images, currentIndex, onClose, onFlag, showFlag, showRightTab, isSmartTagged = false, pageName = "Venue Photo" }: FullImageViewerProps) {
  const i18n = useI18N();
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [mainSwiper, setMainSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'accessibility' | 'review'>(
    images[currentIndex]?.isExperiencePhoto ? 'review' : 'accessibility'
  );
  const [currentZoomLevel, setCurrentZoomLevel] = useState(1);
  const [showAltText, setShowAltText] = useState(false);

  // Add refs for focus management
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const lastFocusedElement = useRef<HTMLElement | null>(null);

  // Add ref for sidebar
  const sidebarRef = useRef<HTMLDivElement>(null);
  const sidebarToggleRef = useRef<HTMLButtonElement>(null);

  const currentImage = images[activeIndex];

  // General-user uploads carry the AI-generated tags, so keying the info panel
  // off the uploader alone hid those tags entirely. Any tagged photo gets it.
  const hasAccessibilityTags = (currentImage?.qtags?.length ?? 0) > 0;

  // Reset to the appropriate default tab when the active image changes
  useEffect(() => {
    setActiveTab(currentImage?.isExperiencePhoto ? 'review' : 'accessibility');
  }, [activeIndex, currentImage?.isExperiencePhoto]);

  // Note: pageName prop is available for future use if FullImageViewer events need to differentiate
  // based on parent context. Currently, FullImageViewer events are self-contained.

  // Shared props for the generated photo_light_box events. The shared lightbox has no
  // place_header_name/place_space_name context → "". business_id = the photo's venueId.
  const photoLightboxBaseProps = () => ({
    business_id:
      currentImage?.venueId != null ? String(currentImage.venueId) : "",
    brand_id: String(currentImage?.venueBrandId ?? ""),
    brand_name: currentImage?.venueBrandName ?? "",
    photo_index: activeIndex,
    // The viewer header renders the photo's tag_name as the space title and category_name as its
    // category, so source the space/header dimensions from the active image instead of leaving blank.
    place_header_name: currentImage?.category_name ?? "",
    place_name: currentImage?.venueName ?? "",
    place_space_name: currentImage?.tag_name ?? "",
  });

  // photo_info_opened / photo_info_closed — one effect covers the desktop sidebar toggle AND the
  // mobile open/close buttons (all flip `showDetails`).
  const prevShowDetailsRef = useRef(showDetails);
  useEffect(() => {
    if (showDetails === prevShowDetailsRef.current) return;
    prevShowDetailsRef.current = showDetails;
    trackPlanEvent(
      showDetails ? "photo_info_opened" : "photo_info_closed",
      photoLightboxBaseProps(),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDetails]);

  // photo_alt_text_toggled — one effect covers all alt-text toggles (mobile + desktop show/hide).
  const prevShowAltTextRef = useRef(showAltText);
  useEffect(() => {
    if (showAltText === prevShowAltTextRef.current) return;
    prevShowAltTextRef.current = showAltText;
    trackPlanEvent("photo_alt_text_toggled", {
      ...photoLightboxBaseProps(),
      alttext_state: showAltText ? "visible" : "hidden",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAltText]);

  // photo_lightbox_opened — centralized here (mirrors photo_lightbox_closed below) so EVERY opener
  // fires it on mount. Previously each call site fired it separately and 7 of 11 did not, producing
  // close-without-open. Ref-guarded for StrictMode. entry_page = the route that opened the lightbox;
  // photos_count = gallery size. place_header_name/place_space_name are "" — the shared viewer has no
  // space context, matching the close/navigate/zoom events emitted from here.
  const lightboxOpenedRef = useRef(false);
  useEffect(() => {
    if (lightboxOpenedRef.current) return;
    lightboxOpenedRef.current = true;
    trackPlanEvent("photo_lightbox_opened", {
      ...photoLightboxBaseProps(),
      entry_page: entryPageFromCurrentPage(),
      photos_count: images.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus management when modal opens/closes
  useEffect(() => {
    // Store the element that had focus before the modal opened
    lastFocusedElement.current = document.activeElement as HTMLElement;

    // Focus the modal container
    if (modalRef.current) {
      modalRef.current.focus();
    }

    // Hide the main content from screen readers
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.setAttribute('aria-hidden', 'true');
    }

    // Add event listener for Escape key
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Set all focusable elements outside the modal to tabindex="-1"
    const focusableElements = Array.from(
      document.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ) as HTMLElement[];

    // Store original tabindex values to restore them later
    const originalTabIndexes = new Map<HTMLElement, string | null>();

    // Make all elements outside the modal non-focusable
    focusableElements.forEach(el => {
      if (!modalRef.current?.contains(el)) {
        originalTabIndexes.set(el, el.getAttribute('tabindex'));
        el.setAttribute('tabindex', '-1');
        el.setAttribute('aria-hidden', 'true');
      }
    });

    return () => {
      // Clean up
      document.removeEventListener('keydown', handleKeyDown);
      
      // Restore focus to the element that had focus before the modal opened
      if (lastFocusedElement.current) {
        lastFocusedElement.current.focus();
      }

      // Remove aria-hidden from main content
      if (mainContent) {
        mainContent.removeAttribute('aria-hidden');
      }

      // Restore original tabindex values
      originalTabIndexes.forEach((value, element) => {
        if (value === null) {
          element.removeAttribute('tabindex');
        } else {
          element.setAttribute('tabindex', value);
        }
        element.removeAttribute('aria-hidden');
      });
    };
  }, [onClose]);

  // Handle tab key to trap focus within the modal more reliably
  const handleTabKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !modalRef.current) return;

    // Get all focusable elements - expanding the selector to ensure we catch all possible elements
    const focusableElementsSelector = 
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable=true]';
    
    const focusableElements = Array.from(
      modalRef.current.querySelectorAll(focusableElementsSelector)
    ).filter(el => {
      // Additional check to make sure the element is visible
      const style = window.getComputedStyle(el as Element);
      return style.display !== 'none' && style.visibility !== 'hidden' && (el as HTMLElement).offsetWidth > 0;
    }) as HTMLElement[];

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Handle regular and shift+tab navigation
    if (e.shiftKey) {
      if (document.activeElement === firstElement || !modalRef.current.contains(document.activeElement)) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement || !modalRef.current.contains(document.activeElement)) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };

  // Handle sidebar toggle
  const handleSidebarToggle = () => {
    const newSidebarState = !showDetails;
    setShowDetails(newSidebarState);

    // Focus management for sidebar
    if (!showDetails) {
      // When opening sidebar, focus the first interactive element in the sidebar
      setTimeout(() => {
        const firstTab = sidebarRef.current?.querySelector('button');
        if (firstTab) {
          firstTab.focus();
        }
      }, 100);
    } else {
      // When closing sidebar, focus the toggle button
      if (sidebarToggleRef.current) {
        sidebarToggleRef.current.focus();
      }
    }
  };

  // Initialize thumbnail swiper to center on the current image when component loads
  useEffect(() => {
    // Wait for thumbsSwiper to be initialized
    if (thumbsSwiper && thumbsSwiper.slideTo) {
      // Use a small timeout to ensure the DOM has fully rendered
      setTimeout(() => {
        // Center the thumbnail swiper on the current image
        thumbsSwiper.slideTo(currentIndex);
      }, 100);
    }
  }, [thumbsSwiper, currentIndex]);

  // Handle tab change
  const PHOTO_INFO_TYPE_BY_TAB: Record<string, string> = {
    accessibility: "accessibility_tags",
    details: "alt_text",
    review: "review",
  };
  const handleTabChange = (tab: 'details' | 'accessibility' | 'review') => {
    // info-panel tab -> photo_info_view_changed (accessibility_tags / alt_text; "review" is the
    // experience-photo tab, off-vocab).
    if (tab !== activeTab) {
      trackPlanEvent("photo_info_view_changed", {
        ...photoLightboxBaseProps(),
        photo_info_type: PHOTO_INFO_TYPE_BY_TAB[tab] ?? tab,
        previous_photo_info_type:
          PHOTO_INFO_TYPE_BY_TAB[activeTab] ?? activeTab,
      });
    }
    setActiveTab(tab);

    // Focus the content of the selected tab
    setTimeout(() => {
      const tabPanel = document.getElementById(`${tab}-tabpanel`);
      if (tabPanel) {
        tabPanel.focus();
      }
    }, 100);
  };

  return (
    <div
      ref={modalRef}
      className={`fixed inset-0 bg-black bg-opacity-90 z-[999] flex flex-col transition-all duration-300 ${showDetails ? "md:pb-0" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Image Viewer"
      onKeyDown={handleTabKey}
      tabIndex={-1}
    >
      <CustomScrollbarStyle />
      {/* Top left bar */}
      <div className="absolute top-4 left-4 z-10 flex gap-2">
        <StandardButton
          ref={closeButtonRef}
          variant="closeModal"
          onClick={() => {
            // close_action_button -> photo_lightbox_closed.
            trackPlanEvent("photo_lightbox_closed", photoLightboxBaseProps());
            onClose();
          }}
          aria-label="Close image viewer"
          tabIndex={1}
          className="flex items-center justify-center text-primary-100 p-2 bg-black bg-opacity-70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          <X size={20} className="text-primary-100" aria-hidden="true" />
        </StandardButton>
      </div>

      {/* Top right bar - only visible on desktop */}
      {showRightTab && (currentImage?.user_roles?.isGeneralUser === false || currentImage?.isExperiencePhoto || hasAccessibilityTags) && (
        <div className="absolute top-4 right-4 z-10 hidden md:block">
          {!showDetails ? (
            <div className="flex items-center gap-2">
              <span className="py-[10px] px-[8px] whitespace-nowrap transition-colors text-sm text-primary-100" tabIndex={4}>
                {currentImage?.isExperiencePhoto ? "Review" : "Accessibility info"}
              </span>
              <StandardButton
                ref={sidebarToggleRef}
                variant="icon"
                onClick={handleSidebarToggle}
                className="flex items-center justify-center text-primary-100 p-2 bg-black bg-opacity-70 rounded-md"
                aria-label="Show accessibility information"
                aria-expanded={showDetails}
                tabIndex={5}
              >
                <PanelRight
                  size={20}
                  className="text-primary-100"
                  aria-hidden="true"
                />
              </StandardButton>
            </div>
          ) : (
            <StandardButton
              ref={sidebarToggleRef}
              variant="icon"
              onClick={handleSidebarToggle}
              className="flex items-center justify-center bg-black bg-opacity-70 text-primary-100 rounded-md p-2"
              aria-label="Hide accessibility information"
              aria-expanded={showDetails}
              tabIndex={5}
            >
              <ArrowRightToLine
                size={20}
                className="text-primary-100"
                aria-hidden="true"
              />
            </StandardButton>
          )}
        </div>
      )}

      {/* Flag button - top right on mobile */}
      {showFlag && (
        <div className="absolute top-4 right-4 z-10 md:hidden">
          <StandardButton
            variant="icon"
            onClick={() => {
              // flag_action_button -> photo_flag_cta_activated.
              trackPlanEvent("photo_flag_cta_activated", photoLightboxBaseProps());
              onFlag(currentImage);
            }}
            className="flex items-center justify-center text-primary-100 p-2 bg-black bg-opacity-70 rounded-md"
            aria-label="Flag photo for review"
            tabIndex={0}
          >
            <PiFlagPennant
              size={20}
              className="text-primary-100"
              aria-hidden="true"
            />
          </StandardButton>
        </div>
      )}

      {/* Bottom right bar - Eye icon for alt text (when bottom sheet is closed) */}
      <div className="fixed bottom-3 right-4 z-50 md:hidden">
        {!showDetails ? (
          /* Eye icon for alt text - now on the right */
          currentImage?.alt && (
            <StandardButton
              variant="icon"
              onClick={() => {
                setShowAltText(!showAltText);
              }}
              className="flex items-center justify-center p-2 bg-black bg-opacity-70 rounded-md"
              aria-label="Toggle alt text"
              tabIndex={0}
            >
              {showAltText ? (
                <EyeOff size={20} className="text-primary-100" />
              ) : (
                <Eye size={20} className="text-primary-100" />
              )}
            </StandardButton>
          )
        ) : null}
      </div>

      {/* Bottom left bar - Info icon for accessibility (when bottom sheet is closed) or Close button (when bottom sheet is open) */}
      <div className="fixed bottom-3 left-4 z-50 md:hidden">
        {showDetails ? (
          <StandardButton
            variant="icon"
            onClick={() => {
              setShowDetails(false);
            }}
            className="flex items-center justify-center text-primary-100 p-2 bg-black rounded-md"
            aria-label="Close details"
            tabIndex={0}
          >
            <ChevronDown size={20} className="text-primary-100" />
          </StandardButton>
        ) : (
          /* Info icon for accessibility information - now on the left */
          <StandardButton
            variant="icon"
            onClick={() => {
              setShowDetails(true);
            }}
            className="flex items-center justify-center text-primary-100 p-2 bg-black rounded-md"
            aria-label="Accessibility information"
            tabIndex={0}
          >
            <Info size={20} className="text-primary-100" />
          </StandardButton>
        )}
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left main viewer */}
        <div
          className={`flex-1 flex flex-col relative overflow-hidden transition-all duration-300 ${showDetails ? "md:translate-y-0 -translate-y-[25vh]" : ""}`}
        >
          {/* Fixed Header - Desktop only */}
          <div className="hidden md:block flex-shrink-0 text-primary-100 text-center py-4 px-6">
            <h2 className="text-base md:text-xl text-primary-100 mb-1">
              {currentImage?.tag_name === "Review Photo" ? (i18n.get("experience").charAt(0).toUpperCase() + i18n.get("experience").slice(1)) : currentImage?.tag_name}              
            </h2>
            <p className="text-xs md:text-sm text-primary-100" tabIndex={3}>
              {currentImage?.isExperiencePhoto
                ? [currentImage.review_user_first_name, currentImage.review_user_last_name].filter(Boolean).join(" ") || null
                : currentImage?.category_name}
              {(currentImage?.isExperiencePhoto
                ? [currentImage.review_user_first_name, currentImage.review_user_last_name].filter(Boolean).join(" ")
                : currentImage?.category_name) && <span className="mx-2">•</span>}
              {/* Calculate position */}
              {currentImage?.isExperiencePhoto
                ? `${activeIndex + 1} of ${images.length}`
                : (() => {
                    const subcategoryImages = images.filter(
                      (img) =>
                        img.tag_name === currentImage?.tag_name &&
                        img.category_name === currentImage?.category_name
                    );
                    const subcategoryPosition =
                      subcategoryImages.findIndex(
                        (img) => img.src === currentImage?.src
                      ) + 1;
                    return `${subcategoryPosition} of ${subcategoryImages.length}`;
                  })()}
            </p>
          </div>

          {/* Mobile Header - optimized for mobile layout */}
          <div className="md:hidden text-primary-100 text-center mb-2 pt-4">
            <h2 className="text-sm text-primary-100 mb-1 px-2 leading-tight">
              {currentImage?.tag_name}
            </h2>
            <div className="text-xs text-primary-100/80 px-2" tabIndex={3}>
              {/* Truncate long category names on mobile */}
              <p className="truncate max-w-[280px] mx-auto mb-1">
                {currentImage?.category_name}
              </p>
              <p className="text-xs">
                {/* Calculate subcategory-specific position */}
                {(() => {
                  // Count images in the same subcategory
                  const subcategoryImages = images.filter(
                    (img) =>
                      img.tag_name === currentImage?.tag_name &&
                      img.category_name === currentImage?.category_name
                  );

                  // Find position within subcategory
                  const subcategoryPosition =
                    subcategoryImages.findIndex(
                      (img) => img.src === currentImage?.src
                    ) + 1;

                  return `${subcategoryPosition} of ${subcategoryImages.length}`;
                })()}
              </p>
            </div>
          </div>

          {/* Fixed Control Layer - Zoom and Flag buttons only */}
          <div className="absolute inset-0 pointer-events-none z-30">
            {/* Flag button - fixed position on left */}
            {showFlag && (
              <button
                onClick={() => {
                  // flag_action_button -> photo_flag_cta_activated.
                  trackPlanEvent("photo_flag_cta_activated", photoLightboxBaseProps());
                  onFlag(currentImage);
                }}
                className="absolute top-1/2 transform -translate-y-1/2 left-4 md:left-8 z-50 text-primary-100 p-2 bg-black bg-opacity-70 rounded-md pointer-events-auto hidden md:block"
                aria-label="Flag photo for review"
                tabIndex={10}
              >
                <PiFlagPennant size={20} aria-hidden="true" />
              </button>
            )}

            {/* Zoom controls - fixed position on right */}
            <div
              className={`absolute top-1/2 transform -translate-y-1/2 right-4 md:right-8 z-20 flex flex-col gap-2 md:gap-3 pointer-events-auto ${showDetails ? "md:flex hidden" : "flex"}`}
            >
              <button
                onClick={() => {
                  if (mainSwiper && mainSwiper.zoom) {
                    mainSwiper.zoom.out();
                    setCurrentZoomLevel(1);
                    // zoom_reset_action_button -> photo_zoomed.
                    trackPlanEvent("photo_zoomed", {
                      ...photoLightboxBaseProps(),
                      zoom_direction: "reset",
                      zoom_level: 1,
                    });
                  }
                }}
                className="flex items-center justify-center text-primary-100 p-2 bg-black bg-opacity-70 rounded-md md:mb-2"
                aria-label="Reset image size"
                tabIndex={11}
              >
                <Shrink size={20} aria-hidden="true" />
              </button>
              <button
                onClick={() => {
                  if (mainSwiper && mainSwiper.zoom) {
                    const newZoomLevel = Math.min(currentZoomLevel + 0.25, 3);
                    setCurrentZoomLevel(newZoomLevel);
                    mainSwiper.zoom.in(newZoomLevel);
                    // zoom_in_action_button -> photo_zoomed.
                    trackPlanEvent("photo_zoomed", {
                      ...photoLightboxBaseProps(),
                      zoom_direction: "in",
                      zoom_level: newZoomLevel,
                    });
                  }
                }}
                className="flex items-center justify-center text-primary-100 p-2 bg-black bg-opacity-70 rounded-md"
                aria-label={`Zoom in to ${Math.round((currentZoomLevel + 0.25) * 100)}%`}
                tabIndex={12}
              >
                <ZoomIn size={20} aria-hidden="true" />
              </button>
              <div className="hidden md:block text-primary-100 text-xs text-center py-1">
                {Math.round(currentZoomLevel * 100)}%
              </div>
              <button
                onClick={() => {
                  if (mainSwiper && mainSwiper.zoom) {
                    const newZoomLevel = Math.max(currentZoomLevel - 0.25, 1);
                    setCurrentZoomLevel(newZoomLevel);
                    // zoom_out_action_button -> photo_zoomed.
                    trackPlanEvent("photo_zoomed", {
                      ...photoLightboxBaseProps(),
                      zoom_direction: "out",
                      zoom_level: newZoomLevel,
                    });

                    if (newZoomLevel === 1) {
                      const currentLevel = currentZoomLevel;
                      const steps = 4;
                      const stepSize = (currentLevel - 1) / steps;
                      
                      for (let i = 1; i <= steps; i++) {
                        setTimeout(() => {
                          const stepLevel = currentLevel - stepSize * i;
                          if (i === steps) {
                            mainSwiper.zoom.out();
                          } else {
                            mainSwiper.zoom.in(stepLevel);
                          }
                        }, i * 30);
                      }
                    } else {
                      mainSwiper.zoom.in(newZoomLevel);
                    }
                  }
                }}
                className="flex items-center justify-center text-primary-100 p-2 bg-black bg-opacity-70 rounded-md"
                aria-label={`Zoom out to ${Math.round((currentZoomLevel - 0.25) * 100) < 100 ? 100 : Math.round((currentZoomLevel - 0.25) * 100)}%`}
                tabIndex={13}
              >
                <ZoomOut size={20} aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Flexible Image Area */}
          <div
            className="flex-1 flex flex-col justify-center items-center relative overflow-hidden"
            role="region"
            aria-roledescription="carousel"
            aria-label={`${currentImage?.tag_name} Image gallery`}
          >
            <Swiper
              modules={[Navigation, Thumbs, Zoom]}
              onSwiper={setMainSwiper}
              onSlideChange={(swiper) => {
                const newIndex = swiper.activeIndex;
                // photo_navigated — centralized for all navigation (prev/next buttons, swipe,
                // thumbnail jumps all route through onSlideChange). photo_view_type "lightbox".
                if (newIndex !== activeIndex) {
                  // Source space/category from the navigated-TO image (the gallery can span spaces),
                  // matching the header that updates as you slide.
                  const navImage = images[newIndex];
                  trackPlanEvent("photo_navigated", {
                    business_id:
                      navImage?.venueId != null
                        ? String(navImage.venueId)
                        : "",
                    brand_id: String(navImage?.venueBrandId ?? ""),
                    brand_name: navImage?.venueBrandName ?? "",
                    navigation_direction:
                      newIndex > activeIndex ? "next" : "previous",
                    photo_collection: "walkthrough",
                    photo_index: newIndex,
                    photo_view_type: "lightbox",
                    place_header_name: navImage?.category_name ?? "",
                    place_name: navImage?.venueName ?? "",
                    place_space_name: navImage?.tag_name ?? "",
                    previous_photo_index: activeIndex,
                  });
                }
                setActiveIndex(newIndex);

                // Reset zoom level when changing slides
                setCurrentZoomLevel(1);
                if (swiper.zoom && swiper.zoom.scale !== 1) {
                  swiper.zoom.out();
                }

                // Explicitly control the thumbnail swiper to center the active thumbnail
                if (thumbsSwiper && thumbsSwiper.slideTo) {
                  // Use a small timeout to ensure the DOM has updated
                  setTimeout(() => {
                    thumbsSwiper.slideTo(newIndex);
                  }, 10);
                }
              }}
              initialSlide={currentIndex}
              zoom
              thumbs={{ swiper: thumbsSwiper }}
              className="w-full h-full"
              aria-label="Main image gallery"
            >
              {images.map((img, idx) => (
                <SwiperSlide
                  key={idx}
                  className="flex justify-center items-center"
                >
                <div className="w-full md:w-2/3 mx-auto bg-black bg-opacity-70 overflow-hidden h-full">
                  <div className="swiper-zoom-container max-h-[75vh] md:max-h-[calc(100vh-160px)] w-full flex justify-center relative px-0 md:px-20">
                    {/* Image container */}
                    <div className="relative inline-flex justify-center items-center">
                      <img
                        src={img.src}
                        alt={img.alt || i18n.get("imageWithoutAltText")}
                        className="object-contain max-h-[75vh] md:max-h-[calc(100vh-200px)] w-full rounded-lg"
                        // aria-label={img.alt || i18n.get("imageWithoutAltText")}
                        aria-label={`${currentImage?.tag_name}, image ${idx + 1} of ${images.length}, ${img.alt ? `. ${img.alt}` : i18n.get("imageWithoutAltText")}, ${i18n.get("viewFullSizeImage")}`}
                        aria-hidden={idx !== activeIndex}
                        tabIndex={idx === activeIndex ? 6 : -1}
                      />

                      {/* Live region for announcing image changes */}
                      <div
                        aria-live="polite"
                        className="sr-only"
                        aria-atomic="true"
                      >
                        {idx === activeIndex && (
                          <>
                            Image {idx + 1} of {images.length}
                            {img.alt
                              ? `: ${img.alt}`
                              : ": Image without description"}
                          </>
                        )}
                      </div>



                      {/* Alt text button */}
                      {img.alt && (
                        <button
                          onClick={() => {
                            setShowAltText(!showAltText);
                          }}
                          className={`absolute bottom-8 right-4 z-20 text-primary-100 bg-black bg-opacity-70 rounded-full px-4 py-2 items-center gap-2 text-sm ${showAltText ? "hidden" : "hidden md:flex"}`}
                          aria-label="Show alt text"
                          aria-hidden={idx !== activeIndex || showAltText}
                          tabIndex={idx === activeIndex ? 7 : -1}
                        >
                          <Captions size={20} className="text-primary-100" />
                          <span>Show alt text</span>
                        </button>
                      )}

                      {/* Alt text display */}
                      {showAltText && img.alt && (
                        <div
                          className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-primary-100 p-4 pb-8 flex-col w-full hidden md:flex"
                          role="presentation"
                        >
                          <div className="flex justify-between items-start" >
                            <div className="flex-1 max-h-[150px] overflow-y-auto pr-4 custom-scrollbar">
                              <p className="font-normal text-sm text-left text-primary-100 transport-options-content-slider"
                                tabIndex={idx === activeIndex ? 0 : -1}
                                dangerouslySetInnerHTML={{ 
                                  __html: img.alt ? 
                                    img.alt.replace(/\r\n|\r|\n/g, '<br />') : '' 
                                }}
                              >
                              </p>
                            </div>
                            <button
                              onClick={() => {
                                setShowAltText(false);
                              }}
                              className="ml-4 p-1 rounded-full hover:bg-black hover:bg-opacity-70 flex-shrink-0 hidden md:block"
                              aria-label="Hide alt text"
                              tabIndex={idx === activeIndex ? 8 : -1}
                            >
                              <EyeOff
                                size={20}
                                className="text-primary-100"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                          <div className="text-xs text-primary-100 mt-2 text-left" tabIndex={-1}>
                            {img.created_at && (
                              <>
                                {new Date(img.created_at).toLocaleDateString()}
                                <span className="mx-2">•</span>
                              </>
                            )}
                            {photoAttribution(img, i18n.get("sociabilityTeam"))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Fixed Footer - Desktop only */}
          <div className="hidden md:block flex-shrink-0">
            {/* Navigation */}
            <div
              className="flex justify-center items-center py-3 relative"
              aria-label="Image gallery navigation"
            >
              <div className="flex items-center space-x-4 px-6 py-2 bg-black bg-opacity-70 rounded-full text-primary-100 text-sm">
                <button
                  onClick={() => {
                    mainSwiper?.slidePrev();
                  }}
                  className="focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50 flex items-center justify-center p-1.5 rounded-full"
                  disabled={activeIndex === 0}
                  aria-label="Previous image"
                  tabIndex={0}
                >
                  <ChevronLeft
                    size={20}
                    className="text-primary-100"
                    aria-hidden="true"
                  />
                </button>
                <span aria-label={`Image ${activeIndex + 1} of ${images.length}`}>
                  {activeIndex + 1}/{images.length}
                </span>
                <button
                  onClick={() => {
                    mainSwiper?.slideNext();
                  }}
                  className="focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50 flex items-center justify-center p-1.5 rounded-full"
                  disabled={activeIndex === images.length - 1}
                  aria-label="Next image"
                  tabIndex={0}
                >
                  <ChevronRight
                    size={20}
                    className="text-primary-100"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>

            {/* Thumbnails */}
            <div
              className="w-full md:w-2/3 mx-auto px-6 pb-2 bg-black bg-opacity-70"
              aria-hidden="true"
            >
              <div className="w-full mx-auto">
                {/* Custom styles for the scrollbar */}
                <style
                  dangerouslySetInnerHTML={{
                    __html: `
                  .swiper-scrollbar {
                    position: relative !important;
                    margin-top: 8px !important;
                    height: 4px !important;
                    background: rgba(255, 255, 255, 0.1) !important;
                    border-radius: 3px !important;
                  }
                  .swiper-scrollbar-drag {
                    background: #A39FE8 !important;
                    border-radius: 3px !important;
                  }
                `,
                  }}
                />
                <div className="relative">
                  <Swiper
                    modules={[Thumbs, Scrollbar, Mousewheel]}
                    onSwiper={setThumbsSwiper}
                    slidesPerView="auto"
                    spaceBetween={8}
                    watchSlidesProgress
                    centeredSlides={true}
                    slideToClickedSlide={true}
                    freeMode={false}
                    scrollbar={{ draggable: true, hide: false }}
                    mousewheel={true}
                    className="w-full mb-0"                 
                  >
                    {images.map((img, idx) => (
                      <SwiperSlide
                        key={idx}
                        style={{ width: "auto", height: 50 }}
                      >
                        <img
                          src={img.src}
                          alt={img.alt || ""}
                          className={`object-contain h-full max-w-none rounded-lg border cursor-pointer ${
                            activeIndex === idx
                              ? "border-blue-500"
                              : "border-transparent"
                          }`}
                          onClick={() => {
                            mainSwiper?.slideTo(idx);
                            setActiveIndex(idx);
                          }}
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile alt text display - preserving original mobile layout */}
          {showAltText && currentImage?.alt && (
            <div className="md:hidden w-full px-4 mb-16">
              <div className="bg-black bg-opacity-70 rounded-md p-3">
                <div className="items-start mb-1">
                  <div className="max-h-[150px] overflow-y-auto pr-1 custom-scrollbar">
                    <p className="font-normal text-sm text-left text-primary-100 transport-options-content-slider"
                      dangerouslySetInnerHTML={{ 
                        __html: currentImage.alt ? 
                          currentImage.alt.replace(/\r\n|\r|\n/g, '<br />') : '' 
                      }}
                    >
                    </p>
                  </div>
                </div>
                <div className="text-xs text-primary-100 text-left">
                  {currentImage?.created_at && (
                    <>
                      {new Date(currentImage.created_at).toLocaleDateString()}
                      <span className="mx-2">•</span>
                    </>
                  )}
                  {photoAttribution(currentImage, i18n.get("sociabilityTeam"))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right info panel toggleable - desktop version (side panel) */}
        {showDetails && (
          <div
            ref={sidebarRef}
            className="hidden md:block w-[320px] bg-black border-l border-gray-700 text-sm text-primary-100 overflow-y-auto relative"
            aria-label="Accessibility information panel"
          >
            {/* Header with photo name and category */}
            <div className="pt-6 pb-2 border-b border-gray-800">
              <h2 className="text-base text-primary-100 text-center">
                {currentImage?.isExperiencePhoto ? "Review" : "Accessibility info"}
              </h2>
            </div>

            {/* Tab navigation */}
            <div className="flex border-b border-gray-800" role="tablist">
              {currentImage?.isExperiencePhoto ? (
                <button
                  className={`flex-1 py-2 px-4 text-center font-medium ${activeTab === "review" ? "text-primary-300 border-b-2 border-primary-300" : "text-primary-100 hover:text-primary-100"}`}
                  onClick={() => handleTabChange("review")}
                  role="tab"
                  aria-selected={activeTab === "review"}
                  aria-controls="review-tabpanel"
                  id="review-tab"
                >
                  Review
                </button>
              ) : (
                <button
                  className={`flex-1 py-2 px-4 text-center font-medium ${activeTab === "accessibility" ? "text-primary-300 border-b-2 border-primary-300" : "text-primary-100 hover:text-primary-100"}`}
                  onClick={() => handleTabChange("accessibility")}
                  role="tab"
                  aria-selected={activeTab === "accessibility"}
                  aria-controls="accessibility-tabpanel"
                  id="accessibility-tab"
                >
                  Accessibility tags
                </button>
              )}
              <button
                className={`flex-1 py-2 px-4 text-center font-medium ${activeTab === "details" ? "text-primary-300 border-b-2 border-primary-300" : "text-primary-100 hover:text-primary-100"}`}
                onClick={() => handleTabChange("details")}
                role="tab"
                aria-selected={activeTab === "details"}
                aria-controls="details-tabpanel"
                id="details-tab"
              >
                Image alt text
              </button>
            </div>

            {/* Tab content */}
            <div className="p-4">
              {/* Review tab (experience photos) */}
              <div
                role="tabpanel"
                id="review-tabpanel"
                aria-labelledby="review-tab"
                hidden={activeTab !== "review"}
                tabIndex={activeTab === "review" ? 0 : -1}
                className="h-[calc(100vh-180px)] overflow-y-auto"
              >
                {activeTab === "review" && (
                  <ReviewTab currentImage={currentImage} />
                )}
              </div>
              {/* Accessibility tags tab */}
              <div
                role="tabpanel"
                id="accessibility-tabpanel"
                aria-labelledby="accessibility-tab"
                hidden={activeTab !== "accessibility"}
                tabIndex={activeTab === "accessibility" ? 0 : -1}
                className="h-[calc(100vh-180px)] overflow-y-auto"
              >
                {activeTab === "accessibility" && (
                  <AccessibilityTagsTab
                    currentImage={currentImage}
                    isSmartTagged={isSmartTagged}
                  />
                )}
              </div>
              {/* Image details tab */}
              <div
                role="tabpanel"
                id="details-tabpanel"
                aria-labelledby="details-tab"
                hidden={activeTab !== "details"}
                tabIndex={activeTab === "details" ? 0 : -1}
                className="h-[calc(100vh-180px)] overflow-y-auto"
              >
                {activeTab === "details" && (
                  <ImageDetailsTab currentImage={currentImage} />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Fixed mobile navigation at the bottom */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 flex justify-center items-center py-3 z-40" role="navigation" aria-label="Image gallery navigation">
          <div className="flex items-center space-x-4 px-6 py-2 bg-black rounded-full text-primary-100 text-sm">
            <button
              onClick={() => {
                mainSwiper?.slidePrev();
              }}
              className="focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50 flex items-center justify-center p-1.5 rounded-full"
              disabled={activeIndex === 0}
              aria-label="Previous image"
              tabIndex={0}
            >
              <ChevronLeft size={20} className="text-primary-100" aria-hidden="true" />
            </button>
            <span aria-label={`Image ${activeIndex + 1} of ${images.length}`}>
              {activeIndex + 1}/{images.length}
            </span>
            <button
              onClick={() => {
                mainSwiper?.slideNext();
              }}
              className="focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50 flex items-center justify-center p-1.5 rounded-full"
              disabled={activeIndex === images.length - 1}
              aria-label="Next image"
              tabIndex={0}
            >
              <ChevronRight size={20} className="text-primary-100" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Mobile bottom sheet version of info panel */}
        {showDetails && (
          <div
            className="md:hidden fixed left-0 right-0 h-[50vh] bg-black border-t border-gray-700 text-sm text-primary-100 overflow-y-auto z-30 rounded-t-xl"
            aria-label="Image details panel"
            style={{ bottom: "48px" }}
          >
            {/* Handle for bottom sheet */}
            <div className="w-full flex justify-center py-2">
              <div className="w-10 h-1 bg-gray-600 rounded-full"></div>
            </div>

            {/* Header with photo name, category, and paging - optimized for mobile */}
            <div className="px-4 pb-2 pt-1 border-b border-gray-800">
              <div className="w-full">
                <p className="text-sm text-primary-100 font-medium leading-tight">
                  {currentImage?.tag_name}
                </p>
                <div className="text-xs text-primary-100/80 mt-1">
                  <p className="truncate">
                    {currentImage?.isExperiencePhoto
                      ? [currentImage.review_user_first_name, currentImage.review_user_last_name].filter(Boolean).join(" ") || null
                      : currentImage?.category_name}
                  </p>
                  <p className="mt-1">
                    {currentImage?.isExperiencePhoto
                      ? `${activeIndex + 1} of ${images.length}`
                      : (() => {
                          const subcategoryImages = images.filter(
                            (img) =>
                              img.tag_name === currentImage?.tag_name &&
                              img.category_name === currentImage?.category_name
                          );
                          const subcategoryPosition =
                            subcategoryImages.findIndex(
                              (img) => img.src === currentImage?.src
                            ) + 1;
                          return `${subcategoryPosition} of ${subcategoryImages.length}`;
                        })()}
                  </p>
                </div>
              </div>
            </div>

            {/* Tab navigation */}
            <div className="flex border-b border-gray-800">
              {currentImage?.isExperiencePhoto ? (
                <button
                  className={`flex-1 py-2 px-4 text-center font-medium ${activeTab === "review" ? "text-primary-300 border-b-2 border-primary-300" : "text-primary-100 hover:text-primary-100"}`}
                  onClick={() => handleTabChange("review")}
                >
                  Review
                </button>
              ) : (
                <button
                  className={`flex-1 py-2 px-4 text-center font-medium ${activeTab === "accessibility" ? "text-primary-300 border-b-2 border-primary-300" : "text-primary-100 hover:text-primary-100"}`}
                  onClick={() => handleTabChange("accessibility")}
                >
                  Accessibility tags
                </button>
              )}
              <button
                className={`flex-1 py-2 px-4 text-center font-medium ${activeTab === "details" ? "text-primary-300 border-b-2 border-primary-300" : "text-primary-100 hover:text-primary-100"}`}
                onClick={() => handleTabChange("details")}
              >
                Image alt text
              </button>
            </div>

            {/* Tab content */}
            <div className="p-4">
              {/* Review tab (experience photos) */}
              {activeTab === "review" && (
                <ReviewTab currentImage={currentImage} />
              )}
              {/* Accessibility tags tab */}
              {activeTab === "accessibility" && (
                <AccessibilityTagsTab
                  currentImage={currentImage}
                  isSmartTagged={isSmartTagged}
                />
              )}
              {/* Image details tab */}
              {activeTab === "details" && (
                <ImageDetailsTab currentImage={currentImage} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}